import React, { useEffect, useState, useRef } from "react";
import { Mic, X, Play, Pause, Volume2, Upload, Download, Sparkles } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { CampaignData } from "../../types";
import { VoiceProps } from "../../../pages/campaign/types";
import useApi from "../../hook/req";

interface CampaignAiProps {
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  uploaded_files: UploadedFile[];
  errors: string[];
  organization_id: string;
}

export interface UploadedFile {
  filename: string;
  s3_key: string;
  content_type: string;
  size: number;
  uploaded_at: number;
  preview_url: string;
}

export const VoiceBroadcast: React.FC<CampaignAiProps> = ({
  campaignData,
  setCampaignData,
}) => {
  // State management
  const [callHandler, setCallHandler] = useState<"ai" | "manual">(() => {
    const saved = localStorage.getItem("callHandler");
    return (saved as "ai" | "manual") || "ai";
  });

  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedVoice, setSelectedVoice] = useState<VoiceProps | null>(null);
  const [messageText, setMessageText] = useState<string>(
    campaignData?.messages || ""
  );
  const [audioUrl, setAudioUrl] = useState<string>(
    campaignData?.audioUrl || ""
  );
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string>("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [voices, setVoices] = useState<VoiceProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<"file" | "url">("file");
  const [isGenerating, setIsGenerating] = useState(false);

  // Voice settings
  const [language, setLanguage] = useState("English (US)");
  const [voiceGender, setVoiceGender] = useState<"male" | "female" | "child">("female");
  const [voiceStyle, setVoiceStyle] = useState("professional");
  const [speed, setSpeed] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);

  const token = localStorage.getItem("authToken");
  const audioRef = useRef<HTMLAudioElement>(null);
  const { makeRequest } = useApi("/media/upload", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const {
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      ...campaignData,
      messages: campaignData?.messages || "",
    },
    mode: "onChange",
  });
  const watchedValues = watch();

  // File handling
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAudioFile(file);

      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac', 'audio/x-m4a'];
      if (!validTypes.includes(file.type)) {
        toast.error("Unsupported file format. Please upload MP3, WAV, OGG, M4A, or AAC files.");
        return;
      }

      try {
        setLoading(true);
        await handleFileUpload(file);
        const objectUrl = URL.createObjectURL(file);
        setPreviewAudioUrl(objectUrl);
        toast.success("Audio file uploaded successfully");
      } catch (error) {
        toast.error("Failed to upload audio file");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("files", file);

    try {
      const [response]: [UploadResponse, number] = await makeRequest(formData);

      if (response.success && response.uploaded_files?.length > 0) {
        const fileUrl = response.uploaded_files[0]?.preview_url;
        setAudioUrl(fileUrl);
        setValue("audioUrl", fileUrl, { shouldValidate: true });
        return response;
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      throw error;
    }
  };

  // Preview handlers
  const handlePreviewMessage = () => {
    if (callHandler === "ai" && !messageText.trim()) {
      toast.error("Please enter a message to preview");
      return;
    }
    setShowPreviewModal(true);
  };

  const handlePreviewAudio = () => {
    if (audioFile && previewAudioUrl) {
      setShowPreviewModal(true);
    } else if (audioUrl.trim()) {
      setPreviewAudioUrl(audioUrl);
      setShowPreviewModal(true);
    } else {
      toast.error("Please upload an audio file or provide an audio URL to preview");
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPreviewPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          toast.error("Failed to play audio");
        });
      }
      setIsPreviewPlaying(!isPreviewPlaying);
    }
  };

  const handleAudioEnded = () => {
    setIsPreviewPlaying(false);
  };

  // Voice selection
  const handleVoiceSelect = (voice: VoiceProps) => {
    setSelectedVoice(voice);
    setSelectedAgent(`${voice.language.toUpperCase()} • ${voice.gender}`);
    setIsAgentDropdownOpen(false);

    // Update voice settings
    setLanguage(`${voice.language} (${voice.gender || "US"})`);
    setVoiceGender(voice.gender as "male" | "female" | "child");

    // Save to localStorage
    localStorage.setItem("selectedVoiceId", voice.voice_id);
    localStorage.setItem("selectedVoiceLanguage", voice.language);
    localStorage.setItem("selectedVoiceGender", voice.gender);
  };

  // Script generation
  const handleGenerateScript = async () => {
    if (!messageText.trim()) {
      toast.error("Please describe your voice message first");
      return;
    }

    setIsGenerating(true);
    try {
      // Call AI script generation API
      // Replace this with your actual API call
      const response = await fetch("https://api.your-ai-service.com/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: messageText,
          language,
          gender: voiceGender,
          style: voiceStyle,
          speed,
          pitch,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate script");
      }

      const data = await response.json();

      // Update message text with generated script
      if (data.script) {
        setMessageText(data.script);
        setValue("messages", data.script, { shouldValidate: true });
      }

      toast.success("Voice script generated successfully");
    } catch (error) {
      toast.error("Failed to generate voice script");
    } finally {
      setIsGenerating(false);
    }
  };

  // Export audio
  const handleExportAudio = async () => {
    if (callHandler === "ai") {
      if (!messageText.trim()) {
        toast.error("Please generate a script first");
        return;
      }

      try {
        // Call text-to-speech API to generate audio
        const response = await fetch("https://api.your-tts-service.com/synthesize", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            text: messageText,
            voice_id: selectedVoice?.voice_id,
            language,
            gender: voiceGender,
            speed,
            pitch,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to generate audio");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `voice-broadcast-${Date.now()}.mp3`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success("Audio exported successfully");
      } catch (error) {
        toast.error("Failed to export audio");
      }
    } else {
      // For manual upload, trigger download of existing audio
      if (audioFile) {
        const url = URL.createObjectURL(audioFile);
        const a = document.createElement("a");
        a.href = url;
        a.download = audioFile.name;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (audioUrl) {
        window.open(audioUrl, "_blank");
      } else {
        toast.error("No audio file available for export");
      }
    }
  };

  // Fetch voices
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const res = await fetch("https://voice.dalmlabs.com/voices");
        const data = await res.json();

        if (Array.isArray(data)) {
          setVoices(data);
        } else if (data?.data && Array.isArray(data.data)) {
          setVoices(data.data);
        } else {
          console.warn("Unexpected response format:", data);
        }
      } catch (error) {
        toast.error("Failed to load voice options");
      }
    };

    fetchVoices();
  }, []);

  // Load saved voice
  useEffect(() => {
    const savedVoiceId = localStorage.getItem("selectedVoiceId");
    if (savedVoiceId && voices.length > 0) {
      const savedVoice = voices.find(v => v.voice_id === savedVoiceId);
      if (savedVoice) {
        handleVoiceSelect(savedVoice);
      }
    }
  }, [voices]);

  // Update campaign data
  useEffect(() => {
    setCampaignData((prev) => ({
      ...prev,
      ...watchedValues,
      smsSegment: prev.smsSegment,
    }));
  }, [watchedValues, setCampaignData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (previewAudioUrl) {
        URL.revokeObjectURL(previewAudioUrl);
      }
    };
  }, [previewAudioUrl]);

  // Calculate word count
  const wordCount = messageText.trim() ? messageText.split(/\s+/).length : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Sidebar */}
          <div className="lg:col-span-1 space-y-4">

            <div className="bg-white rounded-lg shadow-sm p-4">

              <div className="bg-white rounded-lg shadow-sm mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 rounded-lg p-2">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">AI Voice Generator</h2>
                    <p className="text-sm text-gray-500">Create voice scripts</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Active
                  </span>
                </div>
              </div>

              {/* AI/Manual Toggle */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  onClick={() => setCallHandler("ai")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${callHandler === "ai"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  <Mic className="w-4 h-4" />
                  AI
                </button>
                <button
                  onClick={() => setCallHandler("manual")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${callHandler === "manual"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  <Upload className="w-4 h-4" />
                  Manual
                </button>
              </div>

              {callHandler === "ai" ? (
                <>
                  {/* Script Description */}
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <div className="w-1 h-4 bg-blue-600 rounded"></div>
                      Script Description
                    </h3>
                    <Controller
                      name="messages"
                      control={control}
                      rules={{ required: "Script description is required" }}
                      render={({ field, fieldState }) => (
                        <div>
                          <textarea
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e);
                              setMessageText(e.target.value);
                            }}
                            placeholder='Describe your voice message... e.g., "Appointment reminder for dental checkup tomorrow at 2pm"'
                            className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            rows={3}
                          />
                          {fieldState.error && (
                            <p className="text-xs text-red-600 mt-1">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Voice Settings */}
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-blue-600 rounded"></div>
                      Voice Settings
                    </h3>

                    <div className="space-y-3 pl-3">
                      {/* Language */}
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Language</label>
                        <select
                          value={language}
                          onChange={(e) => setLanguage(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="English (US)">English (US)</option>
                          <option value="English (UK)">English (UK)</option>
                          <option value="Spanish">Spanish</option>
                          <option value="French">French</option>
                          <option value="German">German</option>
                        </select>
                      </div>

                      {/* Voice Gender */}
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Voice Gender</label>
                        <select
                          value={voiceGender}
                          onChange={(e) => setVoiceGender(e.target.value as "male" | "female" | "child")}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="child">Child</option>
                        </select>
                      </div>

                      {/* Voice Style */}
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Voice Style</label>
                        <select
                          value={voiceStyle}
                          onChange={(e) => setVoiceStyle(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="professional">Professional</option>
                          <option value="casual">Casual</option>
                          <option value="friendly">Friendly</option>
                          <option value="energetic">Energetic</option>
                          <option value="calm">Calm</option>
                        </select>
                      </div>

                      {/* Speed Slider */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs text-gray-600">Speed</label>
                          <span className="text-xs text-gray-500">{speed.toFixed(1)}x</span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.1"
                          value={speed}
                          onChange={(e) => setSpeed(parseFloat(e.target.value))}
                          className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                          style={{
                            background: `linear-gradient(to right, #1E90FF ${((speed - 0.5) / 1.5) * 100}%, #BFDFFF ${((speed - 0.5) / 1.5) * 100}%)`
                          }}
                        />
                      </div>

                      {/* Pitch Slider */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs text-gray-600">Pitch</label>
                          <span className="text-xs text-gray-500">{pitch.toFixed(1)}x</span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.1"
                          value={pitch}
                          onChange={(e) => setPitch(parseFloat(e.target.value))}
                          className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                          style={{
                            background: `linear-gradient(to right, #1E90FF ${((pitch - 0.5) / 1.5) * 100}%, #BFDFFF ${((pitch - 0.5) / 1.5) * 100}%)`
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerateScript}
                    disabled={isGenerating || !messageText.trim()}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-400 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-blue-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGenerating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate with AI
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* Manual Upload Options */}
                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <button
                        onClick={() => setUploadMethod("file")}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2 ${uploadMethod === "file"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                      >
                        <Upload className="w-3 h-3" />
                        Upload File
                      </button>
                      <button
                        onClick={() => setUploadMethod("url")}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2 ${uploadMethod === "url"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                      >
                        <Volume2 className="w-3 h-3" />
                        Audio URL
                      </button>
                    </div>

                    <div className="space-y-3">
                      {uploadMethod === "file" ? (
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-2 block">
                            Select Audio File
                          </label>
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={handleFileChange}
                            disabled={loading}
                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            {audioFile ? audioFile.name : "No file chosen"}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Supported formats: MP3, WAV, OGG, M4A, AAC
                          </p>
                          {loading && (
                            <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                              <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              Uploading...
                            </p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-2 block">
                            Audio URL
                          </label>
                          <Controller
                            name="audioUrl"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="text"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setAudioUrl(e.target.value);
                                }}
                                placeholder="https://example.com/audio.mp3"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Export Audio Button */}
            {/* <button 
              onClick={handleExportAudio}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Audio
            </button> */}
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {callHandler === "ai" ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Voice Script</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>~{Math.ceil(wordCount / 2.5)}s duration</span>
                      <span>{wordCount} words</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Edit your AI-generated voice message script
                  </p>

                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Script <span className="text-gray-700">*</span>
                    </label>
                    <Controller
                      name="messages"
                      control={control}
                      rules={{ required: "Script is required" }}
                      render={({ field, fieldState }) => (
                        <div>
                          <textarea
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e);
                              setMessageText(e.target.value);
                            }}
                            placeholder="Type or generate your voice script here..."
                            className="w-full p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            rows={8}
                          />
                          {fieldState.error && (
                            <p className="text-xs text-red-600 mt-1">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {messageText.length} characters • {wordCount} words
                    </p>
                  </div>

                  {/* Preview Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Preview</h4>
                      <button
                        onClick={handlePreviewMessage}
                        disabled={!messageText.trim()}
                        className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Preview
                      </button>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px]">
                      {messageText ? (
                        <div className="w-full">
                          <div className="bg-white p-4 rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{messageText}</p>
                          </div>
                          <div className="mt-4 text-center">
                            <p className="text-xs text-gray-500">
                              Voice Settings: {language} • {voiceGender} • {voiceStyle} • Speed: {speed}x • Pitch: {pitch}x
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                          </div>
                          <p className="text-gray-500 text-sm text-center">
                            Enter a prompt and generate your voice script
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Uploaded Audio Preview</h3>
                    {(audioFile || audioUrl) && (
                      <button
                        onClick={handlePreviewAudio}
                        className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Preview Audio
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-6">
                    Preview your uploaded audio file
                  </p>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center min-h-[400px]">
                    {audioFile || audioUrl ? (
                      <div className="w-full text-center">
                        <div className="text-gray-400 mb-4">
                          <Volume2 className="w-16 h-16 mx-auto" />
                        </div>
                        <p className="text-gray-700 font-medium mb-2">
                          {audioFile ? `File: ${audioFile.name}` : `URL: ${audioUrl.substring(0, 50)}...`}
                        </p>
                        {audioFile && (
                          <p className="text-gray-500 text-sm mb-4">
                            Size: {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={handlePreviewAudio}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <Play className="w-4 h-4" />
                            Play Audio
                          </button>
                          <button
                            onClick={handleExportAudio}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-gray-400 mb-4">
                          <Upload className="w-16 h-16" />
                        </div>
                        <p className="text-gray-600 text-sm font-medium mb-2">
                          Upload an audio file or paste an audio URL
                        </p>
                        <p className="text-gray-400 text-xs">
                          Use the options on the left to get started
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {callHandler === "ai" ? "Message Preview" : "Audio Preview"}
              </h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setIsPreviewPlaying(false);
                  if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {callHandler === "ai" ? (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Voice Settings</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Language:</strong> {language}</p>
                    <p><strong>Gender:</strong> {voiceGender}</p>
                    <p><strong>Style:</strong> {voiceStyle}</p>
                    <p><strong>Speed:</strong> {speed.toFixed(1)}x</p>
                    <p><strong>Pitch:</strong> {pitch.toFixed(1)}x</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Script</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{messageText}</p>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handlePlayPause}
                    disabled={!selectedVoice}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPreviewPlaying ? (
                      <>
                        <Pause className="w-5 h-5" />
                        Pause Preview
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        Play Preview
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Audio Source</h4>
                  <p className="text-sm text-gray-600 break-all">
                    {audioFile ? `File: ${audioFile.name}` : `URL: ${audioUrl}`}
                  </p>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handlePlayPause}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {isPreviewPlaying ? (
                      <>
                        <Pause className="w-5 h-5" />
                        Pause Audio
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        Play Audio
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <audio
              ref={audioRef}
              src={previewAudioUrl}
              onEnded={handleAudioEnded}
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceBroadcast;